import {
    CroppedRegion,
    DetectedItem,
    ImageAnalysisCategoryHint,
    ImageAnalysisIssueCode,
    ImageAnalysisProviderMetadata,
    ImageAnalysisProviderSourceStatus,
    ImageAnalysisRequest,
    ImageAnalysisResponse,
    ImageAnalysisSelectedRegionCandidate,
    ImageAnalysisStatus,
    ImageEmbeddingSearchHint,
    ImageEmbeddingSearchRequest,
    ImageEmbeddingSearchResponse,
    LookitItemCategoryId,
    ProductSubcategory
} from './domain';
import { defaultRegionForCategory } from './category-default-regions';
import {
    createImageEmbeddingForSearchRequest,
    EmbeddingProvider,
    ImageEmbeddingCreateResponse,
    mockEmbeddingProvider
} from './embedding-provider';
import {
    mockVectorSearchRepository,
    vectorSearchResponseToImageEmbeddingSearchResponse,
    VectorSearchCategoryFilter,
    VectorSearchRepository
} from './vector-search-repository';
import {
    ImageAnalysisProvider,
    ImageAnalysisProviderCropAnalysisRequest,
    ImageAnalysisProviderDetectionResponse,
    ImageAnalysisProviderEmbeddingCandidatesResponse,
    ImageAnalysisProviderEmbeddingResponse,
    ImageAnalysisProviderId,
    ImageAnalysisProviderOcrHintsResponse
} from './image-analysis-provider';

export type FakeImageAnalysisScenario =
    | 'normal'
    | 'low_confidence'
    | 'no_detection'
    | 'multiple_items_confused'
    | 'unsupported_image'
    | 'non_fashion_image'
    | 'provider_timeout'
    | 'privacy_blocked'
    | 'embedding_candidates_empty';

export interface FakeImageAnalysisProviderOptions {
    id?: ImageAnalysisProviderId;
    label?: string;
    modelVersion?: string;
    scenario?: FakeImageAnalysisScenario | null;
    embeddingProvider?: EmbeddingProvider;
    vectorSearchRepository?: VectorSearchRepository;
}

const autoCategoryMinConfidence = 0.8;

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function usableRegion(region?: CroppedRegion | null): CroppedRegion | null {
    if (!region) return null;
    if (region.width <= 0 || region.height <= 0) return null;
    return region;
}

function regionForCategory(categoryId: LookitItemCategoryId | string): CroppedRegion {
    return cloneData(defaultRegionForCategory(categoryId) || defaultRegionForCategory('other')!);
}

function categoryHintForRequest(request: ImageAnalysisRequest): ImageAnalysisCategoryHint | null {
    if (typeof request.categoryHint === 'string' && request.categoryHint.trim()) {
        return {
            categoryId: request.categoryHint,
            source: 'user_selected',
            confidence: request.userConfirmed ? 1 : null
        };
    }

    if (request.categoryHint && typeof request.categoryHint === 'object' && 'categoryId' in request.categoryHint) {
        return request.categoryHint as ImageAnalysisCategoryHint;
    }

    if (request.selectedCategoryId) {
        return {
            categoryId: request.selectedCategoryId,
            source: 'user_selected',
            confidence: request.userConfirmed ? 1 : null
        };
    }

    return (request.categoryHints || [])
        .slice()
        .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))[0] || null;
}

function bestRegionCandidate(candidates: ImageAnalysisSelectedRegionCandidate[] = []): ImageAnalysisSelectedRegionCandidate | null {
    return candidates
        .slice()
        .filter((candidate) => Boolean(usableRegion(candidate.region)))
        .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))[0] || null;
}

function selectedRegionHasUserPriority(request: ImageAnalysisRequest): boolean {
    return Boolean(
        usableRegion(request.selectedRegion) &&
        (
            request.userConfirmed === true ||
            request.regionSource === 'manual' ||
            request.regionSource === 'adjusted' ||
            request.regionSource === 'full_image'
        )
    );
}

function detectedItemRegion(item: DetectedItem): CroppedRegion | null {
    return usableRegion(item.cropped_region) || usableRegion(item.boundingBox);
}

function categoryConfidenceForDetectedItem(item: DetectedItem): number | null {
    const confidence = item.suggestedCategoryConfidence ?? item.categoryConfidence ?? null;
    return typeof confidence === 'number' && Number.isFinite(confidence) ? confidence : null;
}

function detectedCandidatePriority(item: DetectedItem): number {
    const confirmationPenalty = item.needsUserConfirmation ? 0.15 : 0;
    return item.confidence - confirmationPenalty;
}

function compareDetectedCandidates(a: DetectedItem, b: DetectedItem): number {
    const priorityDiff = detectedCandidatePriority(b) - detectedCandidatePriority(a);
    if (priorityDiff !== 0) return priorityDiff;
    return a.item_index - b.item_index;
}

function bestDetectedCandidate(items: DetectedItem[]): DetectedItem | null {
    return items
        .slice()
        .filter((item) => Boolean(detectedItemRegion(item)))
        .sort(compareDetectedCandidates)[0] || null;
}

function itemNeedsConfirmation(item: DetectedItem): boolean {
    const categoryConfidence = categoryConfidenceForDetectedItem(item);
    return Boolean(
        item.needsUserConfirmation ||
        item.confidence < 0.65 ||
        categoryConfidence === null ||
        categoryConfidence < autoCategoryMinConfidence
    );
}

function issueMessage(status: ImageAnalysisStatus, sourceStatus?: ImageAnalysisProviderSourceStatus): string | null {
    if (sourceStatus === 'provider_timeout') return '이미지 분석 provider 응답 시간이 초과되어 수동 영역 선택으로 전환합니다.';
    if (sourceStatus === 'privacy_blocked') return '개인정보 보호 gate가 외부 이미지 분석 전송을 차단했습니다.';
    if (status === 'no_item_detected') return '자동 감지 후보가 없어 사용자가 직접 영역을 선택해야 합니다.';
    if (status === 'low_confidence') return '자동 감지 신뢰도가 낮아 영역과 카테고리 확인이 필요합니다.';
    if (status === 'multiple_items_confused') return '여러 의류 후보가 겹쳐 사용자가 찾을 영역을 직접 확정해야 합니다.';
    if (status === 'unsupported_image') return '지원하지 않는 이미지 형식입니다. jpg, png, webp 이미지로 다시 시도해 주세요.';
    if (status === 'non_fashion_image') return '패션 상품으로 판단할 수 없는 이미지입니다. 옷이 잘 보이는 사진으로 다시 시도해 주세요.';
    return null;
}

function sourceStatusForStatus(status: ImageAnalysisStatus): ImageAnalysisProviderSourceStatus {
    if (status === 'detected') return 'mock';
    if (status === 'no_item_detected') return 'no_detection';
    return 'low_confidence';
}

function statusFromDetectedItems(items: DetectedItem[]): ImageAnalysisStatus {
    if (items.length === 0) return 'no_item_detected';
    const ranked = items.slice().sort(compareDetectedCandidates);
    const first = ranked[0] || null;
    const second = ranked[1] || null;
    if (!first) return 'no_item_detected';
    if (second && Math.abs(detectedCandidatePriority(first) - detectedCandidatePriority(second)) < 0.04 && first.confidence < 0.75) {
        return 'multiple_items_confused';
    }
    return itemNeedsConfirmation(first) ? 'low_confidence' : 'detected';
}

function responseFromDetectedItems(items: DetectedItem[], forcedStatus?: ImageAnalysisStatus): ImageAnalysisResponse {
    const detectedItems = cloneData(items);
    const status = forcedStatus || statusFromDetectedItems(detectedItems);
    const bestItem = status === 'multiple_items_confused' ? null : bestDetectedCandidate(detectedItems);
    const bestRegion = bestItem ? detectedItemRegion(bestItem) : null;
    const issueCode = status === 'detected' ? null : status as ImageAnalysisIssueCode;

    return {
        status,
        detectedItems,
        suggestedRegion: bestRegion ? cloneData(bestRegion) : null,
        suggestedCategoryId: bestItem?.suggestedCategoryId || null,
        confidence: bestItem?.confidence ?? (detectedItems[0]?.confidence ?? null),
        needsUserConfirmation: status !== 'detected' || Boolean(bestItem && itemNeedsConfirmation(bestItem)),
        issueCode,
        message: issueMessage(status)
    };
}

function mockDetectedTemplates(): DetectedItem[] {
    const topRegion = regionForCategory('top');
    const bottomRegion = regionForCategory('bottom');
    const shoesRegion = regionForCategory('shoes');

    return [
        {
            id: 'mock-detected-top-blouse',
            item_index: 0,
            label: '상의 > 블라우스',
            category: 'top',
            confidence: 0.86,
            suggestedCategoryId: 'top',
            suggestedCategoryConfidence: 0.84,
            categoryConfidence: 0.84,
            boundingBox: topRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: false,
            confirmationReason: null,
            colors: ['크림'],
            cropped_region: topRegion
        },
        {
            id: 'mock-detected-bottom-pants',
            item_index: 1,
            label: '하의 > 데님',
            category: 'bottom',
            confidence: 0.79,
            suggestedCategoryId: 'bottom',
            suggestedCategoryConfidence: 0.76,
            categoryConfidence: 0.76,
            boundingBox: bottomRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: false,
            confirmationReason: null,
            colors: ['중청'],
            cropped_region: bottomRegion
        },
        {
            id: 'mock-detected-shoes',
            item_index: 2,
            label: '신발',
            category: 'shoes',
            confidence: 0.52,
            suggestedCategoryId: 'shoes',
            suggestedCategoryConfidence: 0.46,
            categoryConfidence: 0.46,
            boundingBox: shoesRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: true,
            confirmationReason: 'low_confidence',
            colors: ['블랙'],
            cropped_region: shoesRegion
        }
    ];
}

function mockLowConfidenceItems(): DetectedItem[] {
    const bottomRegion = regionForCategory('bottom');

    return [
        {
            id: 'mock-low-confidence-bottom',
            item_index: 0,
            label: '하의 후보',
            category: 'bottom',
            confidence: 0.58,
            suggestedCategoryId: 'bottom',
            suggestedCategoryConfidence: 0.42,
            categoryConfidence: 0.42,
            boundingBox: bottomRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: true,
            confirmationReason: 'low_confidence',
            colors: ['중청'],
            cropped_region: bottomRegion
        }
    ];
}

function mockMultipleConfusedItems(): DetectedItem[] {
    const topRegion = regionForCategory('top');
    const outerRegion = regionForCategory('outer');

    return [
        {
            id: 'mock-confused-top',
            item_index: 0,
            label: '상의 후보',
            category: 'top',
            confidence: 0.62,
            suggestedCategoryId: 'top',
            suggestedCategoryConfidence: 0.56,
            categoryConfidence: 0.56,
            boundingBox: topRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: true,
            confirmationReason: 'multi_item_overlap',
            colors: ['크림'],
            cropped_region: topRegion
        },
        {
            id: 'mock-confused-outer',
            item_index: 1,
            label: '아우터 후보',
            category: 'outer',
            confidence: 0.61,
            suggestedCategoryId: 'outer',
            suggestedCategoryConfidence: 0.55,
            categoryConfidence: 0.55,
            boundingBox: outerRegion,
            maskUrl: null,
            maskPolygon: null,
            source: 'mock',
            needsUserConfirmation: true,
            confirmationReason: 'multi_item_overlap',
            colors: ['아이보리'],
            cropped_region: outerRegion
        }
    ];
}

function scenarioText(request: ImageAnalysisRequest | ImageEmbeddingSearchRequest): string {
    const scenario = (request as unknown as { mockScenario?: unknown }).mockScenario;
    return [
        typeof scenario === 'string' ? scenario : '',
        'localMockImageId' in request ? request.localMockImageId || '' : '',
        request.uploadImageId || '',
        'cropImageId' in request ? request.cropImageId || '' : '',
        'fileName' in request ? request.fileName || '' : ''
    ].join(' ').toLowerCase();
}

function scenarioForRequest(
    request: ImageAnalysisRequest | ImageEmbeddingSearchRequest,
    configuredScenario?: FakeImageAnalysisScenario | null
): FakeImageAnalysisScenario {
    if (configuredScenario) return configuredScenario;
    const text = scenarioText(request);
    if (/provider[-_ ]?timeout|timeout/.test(text)) return 'provider_timeout';
    if (/privacy[-_ ]?blocked|privacy[-_ ]?gate/.test(text)) return 'privacy_blocked';
    if (/embedding[-_ ]?candidates[-_ ]?empty|no[-_ ]?embedding/.test(text)) return 'embedding_candidates_empty';
    if (/unsupported|heic|gif|pdf|bmp|tiff/.test(text)) return 'unsupported_image';
    if (/non[-_ ]?fashion|not[-_ ]?fashion|landscape|food|document|receipt/.test(text)) return 'non_fashion_image';
    if (/no[-_ ]?(item|detection)|empty|none/.test(text)) return 'no_detection';
    if (/multiple|confused|multi/.test(text)) return 'multiple_items_confused';
    if (/low[-_ ]?confidence|uncertain/.test(text)) return 'low_confidence';
    return 'normal';
}

function responseFromRegionCandidate(request: ImageAnalysisRequest): ImageAnalysisResponse | null {
    const hint = categoryHintForRequest(request);
    const directRegion = usableRegion(request.selectedRegion);
    const candidate = directRegion
        ? {
            id: 'selected-region',
            region: directRegion,
            source: 'manual' as const,
            categoryId: hint?.categoryId || null,
            confidence: request.userConfirmed ? 0.92 : hint?.confidence ?? 0.82
        }
        : bestRegionCandidate(request.selectedRegionCandidates);
    if (!candidate) return null;

    const confidence = candidate.confidence ?? hint?.confidence ?? 0.5;
    const suggestedCategoryId = candidate.categoryId || hint?.categoryId || null;
    const status: ImageAnalysisStatus = confidence >= autoCategoryMinConfidence ? 'detected' : 'low_confidence';
    const issueCode = status === 'detected' ? null : status as ImageAnalysisIssueCode;

    return {
        status,
        detectedItems: mockDetectedTemplates(),
        suggestedRegion: cloneData(candidate.region),
        suggestedCategoryId,
        confidence,
        needsUserConfirmation: status !== 'detected',
        issueCode,
        message: issueMessage(status)
    };
}

function emptyEmbeddingResponse(
    request: ImageEmbeddingSearchRequest,
    sourceStatus: ImageAnalysisProviderSourceStatus
): ImageEmbeddingSearchResponse {
    return {
        sourceRequestId: request.sourceRequestId,
        selectedCategoryId: request.selectedCategoryId,
        sourceStatus: sourceStatus === 'mock' ? 'mock' : 'api_pending',
        candidates: [],
        sections: [
            { section: 'brand_exact', label: '동일 상품 가능성 높음', candidates: [] },
            { section: 'wholesale_same', label: '같은 도매택/동일 공급 상품 추정', candidates: [] },
            { section: 'similar_style', label: '유사 디자인 상품', candidates: [] }
        ]
    };
}

function vectorFilterForEmbeddingRequest(request: ImageEmbeddingSearchRequest): VectorSearchCategoryFilter {
    return {
        selectedCategoryId: request.selectedCategoryId,
        subcategory: request.subcategoryHint as ProductSubcategory | string | null,
        subcategoryConfirmed: Boolean(request.subcategoryHint),
        brandHints: request.brandHints || [],
        ocrHints: request.ocrHints || [],
        wholesaleTagHints: (request.brandHints || []).filter((hint) => hint.source === 'wholesale_tag'),
        includeMismatchedCandidates: false
    };
}

export class FakeImageAnalysisProvider implements ImageAnalysisProvider {
    public readonly id: ImageAnalysisProviderId;
    public readonly label: string;
    public readonly kind = 'mock' as const;
    public readonly capabilities = [
        'garment_analysis',
        'crop_analysis',
        'embedding',
        'ocr_hints'
    ] as const;
    public readonly requiresExternalTransferGuard = false;

    private readonly modelVersion: string;
    private readonly scenario: FakeImageAnalysisScenario | null;
    private readonly embeddingProvider: EmbeddingProvider;
    private readonly vectorSearchRepository: VectorSearchRepository;

    constructor(options: FakeImageAnalysisProviderOptions = {}) {
        this.id = options.id || 'mock';
        this.label = options.label || 'LOOKIT fake image analysis provider';
        this.modelVersion = options.modelVersion || 'lookit-fake-image-analysis-v1';
        this.scenario = options.scenario || null;
        this.embeddingProvider = options.embeddingProvider || mockEmbeddingProvider;
        this.vectorSearchRepository = options.vectorSearchRepository || mockVectorSearchRepository;
    }

    getMetadata(
        sourceStatus: ImageAnalysisProviderSourceStatus = 'mock',
        confidence: number | null = null
    ): ImageAnalysisProviderMetadata {
        return {
            providerId: this.id,
            providerLabel: this.label,
            providerKind: this.kind,
            sourceStatus,
            confidence,
            modelVersion: this.modelVersion,
            externalCall: false,
            statusDetail: null
        };
    }

    async detectItems(request: ImageAnalysisRequest): Promise<ImageAnalysisProviderDetectionResponse> {
        const scenario = scenarioForRequest(request, this.scenario);
        let response: ImageAnalysisResponse;
        let sourceStatus: ImageAnalysisProviderSourceStatus;

        if (selectedRegionHasUserPriority(request)) {
            response = responseFromRegionCandidate(request) || responseFromDetectedItems(mockDetectedTemplates());
            sourceStatus = 'mock';
        } else if (scenario === 'provider_timeout') {
            response = {
                status: 'low_confidence',
                detectedItems: [],
                suggestedRegion: null,
                suggestedCategoryId: null,
                confidence: null,
                needsUserConfirmation: true,
                issueCode: 'low_confidence',
                message: issueMessage('low_confidence', 'provider_timeout')
            };
            sourceStatus = 'provider_timeout';
        } else if (scenario === 'privacy_blocked') {
            response = {
                status: 'low_confidence',
                detectedItems: [],
                suggestedRegion: null,
                suggestedCategoryId: null,
                confidence: null,
                needsUserConfirmation: true,
                issueCode: 'low_confidence',
                message: issueMessage('low_confidence', 'privacy_blocked')
            };
            sourceStatus = 'privacy_blocked';
        } else if (scenario === 'unsupported_image') {
            response = responseFromDetectedItems([], 'unsupported_image');
            sourceStatus = 'low_confidence';
        } else if (scenario === 'non_fashion_image') {
            response = responseFromDetectedItems([], 'non_fashion_image');
            sourceStatus = 'low_confidence';
        } else if (scenario === 'no_detection') {
            response = responseFromDetectedItems([], 'no_item_detected');
            sourceStatus = 'no_detection';
        } else if (scenario === 'multiple_items_confused') {
            response = responseFromDetectedItems(mockMultipleConfusedItems(), 'multiple_items_confused');
            sourceStatus = 'low_confidence';
        } else if (scenario === 'low_confidence') {
            response = responseFromDetectedItems(mockLowConfidenceItems(), 'low_confidence');
            sourceStatus = 'low_confidence';
        } else {
            response = responseFromRegionCandidate(request) || responseFromDetectedItems(mockDetectedTemplates());
            sourceStatus = sourceStatusForStatus(response.status);
        }

        const providerMetadata = this.getMetadata(sourceStatus, response.confidence);
        return {
            ...response,
            sourceStatus,
            providerMetadata
        };
    }

    async analyzeGarments(request: ImageAnalysisRequest): Promise<ImageAnalysisProviderDetectionResponse> {
        return this.detectItems(request);
    }

    async requestCropAnalysis(request: ImageAnalysisProviderCropAnalysisRequest): Promise<ImageAnalysisProviderDetectionResponse> {
        return this.detectItems({
            ...request,
            selectedRegion: cloneData(request.selectedRegion),
            selectedCategoryId: request.selectedCategoryId,
            userConfirmed: request.userConfirmed ?? true
        });
    }

    async createEmbedding(request: ImageEmbeddingSearchRequest): Promise<ImageAnalysisProviderEmbeddingResponse> {
        const embedding: ImageEmbeddingCreateResponse = await createImageEmbeddingForSearchRequest(
            this.embeddingProvider,
            request
        );
        const sourceStatus: ImageAnalysisProviderSourceStatus = 'mock';

        return {
            ...embedding,
            sourceStatus,
            providerMetadata: this.getMetadata(sourceStatus, 1)
        };
    }

    async getEmbeddingCandidates(
        request: ImageEmbeddingSearchRequest,
        limit = 20
    ): Promise<ImageAnalysisProviderEmbeddingCandidatesResponse> {
        const scenario = scenarioForRequest(request, this.scenario);
        if (scenario === 'embedding_candidates_empty') {
            const sourceStatus: ImageAnalysisProviderSourceStatus = 'no_detection';
            return {
                ...emptyEmbeddingResponse(request, sourceStatus),
                sourceStatus,
                imageEmbeddingSourceStatus: 'api_pending',
                confidence: null,
                providerMetadata: this.getMetadata(sourceStatus, null)
            };
        }

        try {
            const embedding = await this.createEmbedding(request);
            const vectorResponse = await this.vectorSearchRepository.searchSimilarProducts(
                embedding.vectorId,
                vectorFilterForEmbeddingRequest(request),
                limit
            );
            const response = vectorSearchResponseToImageEmbeddingSearchResponse(vectorResponse, {
                sourceRequestId: request.sourceRequestId,
                selectedCategoryId: request.selectedCategoryId
            });
            const confidence = response.candidates.reduce((best, candidate) => Math.max(best, candidate.finalScore), 0);
            const sourceStatus: ImageAnalysisProviderSourceStatus = response.candidates.length > 0 ? 'mock' : 'no_detection';

            return {
                ...response,
                sourceStatus,
                imageEmbeddingSourceStatus: response.sourceStatus,
                confidence,
                providerMetadata: this.getMetadata(sourceStatus, confidence || null)
            };
        } catch {
            const sourceStatus: ImageAnalysisProviderSourceStatus = 'no_detection';
            return {
                ...emptyEmbeddingResponse(request, sourceStatus),
                sourceStatus,
                imageEmbeddingSourceStatus: 'api_pending',
                confidence: null,
                providerMetadata: this.getMetadata(sourceStatus, null)
            };
        }
    }

    async analyzeOcrHints(request: ImageAnalysisRequest): Promise<ImageAnalysisProviderOcrHintsResponse> {
        const categoryId = categoryHintForRequest(request)?.categoryId || request.selectedCategoryId || 'top';
        const hints: ImageEmbeddingSearchHint[] = categoryId === 'bottom'
            ? [
                {
                    id: 'fake-ocr-muse-denim',
                    source: 'ocr',
                    text: 'MUSE Brand denim',
                    normalizedText: 'muse brand denim',
                    confidence: 0.74,
                    boundingBox: null
                }
            ]
            : [
                {
                    id: 'fake-ocr-liette',
                    source: 'ocr',
                    text: 'LIETTE',
                    normalizedText: 'liette',
                    confidence: 0.82,
                    boundingBox: null
                }
            ];
        const confidence = hints.reduce((best, hint) => Math.max(best, hint.confidence ?? 0), 0);
        const sourceStatus: ImageAnalysisProviderSourceStatus = hints.length > 0 ? 'mock' : 'no_detection';

        return {
            hints,
            sourceStatus,
            confidence,
            providerMetadata: this.getMetadata(sourceStatus, confidence)
        };
    }
}

export const fakeImageAnalysisProvider = new FakeImageAnalysisProvider();
